import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Loader2, FileDown, RefreshCw, CheckCircle2, FileText, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface TransferDutyPanelProps {
  companyId: string;
  jurisdiction: string;
  currency?: string;
}

export default function TransferDutyPanel({
  companyId,
  jurisdiction,
  currency = 'USD',
}: TransferDutyPanelProps) {
  const { toast } = useToast();

  // The calculation states its own currency, which is a property of the
  // jurisdiction whose duty rules were applied — South African Securities
  // Transfer Tax is payable in rand whatever the caller assumed. The prop is
  // only a placeholder for before a calculation has run.
  const [registerRows, setRegisterRows] = useState<any[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [calc, setCalc] = useState<any | null>(null);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [paidProof, setPaidProof] = useState<File | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [{ data: reg }, { data: prof }] = await Promise.all([
        supabase
          .from('share_register')
          .select(
            'id, holder_name, share_class, shares, price_per_share, consideration, status, issue_date, transfer_date, duties_paid'
          )
          .eq('company_id', companyId)
          .in('status', ['TRANSFERRED', 'ISSUED']),
        supabase.from('duty_profiles').select('*'),
      ]);
      setRegisterRows(reg || []);
      setProfiles(prof || []);
    } catch (e: any) {
      toast({
        title: 'Error loading data',
        description: e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function runCalc() {
    if (!selected) {
      toast({
        title: 'Selection required',
        description: 'Choose a register entry',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('duty-calc', {
        body: { registerId: selected },
      });
      if (error) throw error;
      setCalc(data);
      toast({
        title: 'Duty calculated',
        description: `Total payable: ${data.currency ?? currency} ${Number(data.total_payable).toLocaleString()}`,
      });
    } catch (e: any) {
      toast({
        title: 'Calculation failed',
        description: e.message,
        variant: 'destructive',
      });
    }
  }

  async function generateForm() {
    if (!selected) {
      toast({
        title: 'Selection required',
        description: 'Choose a register entry',
        variant: 'destructive',
      });
      return;
    }

    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-transfer-form', {
        body: { registerId: selected, includeDeclaration: true },
      });
      if (error) throw error;

      toast({
        title: 'Transfer form generated',
        description: 'Opening form in new window',
      });
      window.open(data?.url, '_blank');
    } catch (e: any) {
      toast({
        title: 'Generation failed',
        description: e.message,
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  }

  async function markPaid() {
    if (!selected) {
      toast({
        title: 'Selection required',
        description: 'Choose a register entry',
        variant: 'destructive',
      });
      return;
    }

    try {
      let proofUrl = null;
      if (paidProof) {
        const path = `${companyId}/duty-proof/${selected}-${Date.now()}-${paidProof.name}`;
        const { error: upErr } = await supabase.storage.from('duty-forms').upload(path, paidProof);
        if (upErr) throw upErr;
        proofUrl = path;
      }

      const { error } = await supabase
        .from('share_certificates')
        .update({ duties_paid: true })
        .eq('id', selected);
      if (error) throw error;

      toast({
        title: 'Duties marked as paid',
        description: proofUrl ? 'Proof of payment uploaded' : 'Status updated',
      });
      load();
      setSelected('');
      setPaidProof(null);
    } catch (e: any) {
      toast({
        title: 'Update failed',
        description: e.message,
        variant: 'destructive',
      });
    }
  }

  useEffect(() => {
    load();
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Transfer Duty & Tax Forms ({jurisdiction})
        </CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Register picker */}
        <div className="grid sm:grid-cols-3 gap-2 items-end">
          <div className="space-y-1">
            <label className="text-sm font-medium">Select Share Register Entry</label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger>
                <SelectValue placeholder="Choose entry..." />
              </SelectTrigger>
              <SelectContent>
                {registerRows.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.holder_name} • {r.share_class} • {Number(r.shares).toLocaleString()}
                    {r.duties_paid ? ' ✓' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={runCalc} disabled={!selected || loading}>
            Calculate Duty
          </Button>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <AlertCircle className="h-3 w-3" />
            Not tax advice. Verify rates locally.
          </div>
        </div>

        {/* Calc result */}
        {calc && (
          <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Consideration:</span>
                <div className="font-semibold">
                  {calc.currency ?? currency} {Number(calc.consideration || 0).toLocaleString()}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Method:</span>
                <div className="font-semibold">{calc.method}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Rate:</span>
                <div className="font-semibold">{calc.rate_pct ? `${calc.rate_pct}%` : '—'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Rounding:</span>
                <div className="font-semibold">{calc.rounding || 'NONE'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Min Amount:</span>
                <div className="font-semibold">
                  {calc.min_amount
                    ? `${calc.currency ?? currency} ${Number(calc.min_amount).toLocaleString()}`
                    : '—'}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Transfer Duty:</span>
                <div className="font-semibold text-primary">
                  {calc.currency ?? currency} {Number(calc.duty_amount || 0).toLocaleString()}
                </div>
              </div>
            </div>

            {Array.isArray(calc.fee_items) && calc.fee_items.length > 0 && (
              <div className="border-t pt-2">
                <div className="text-sm font-medium mb-1">Additional Fees</div>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  {calc.fee_items.map((f: any) => (
                    <li key={f.label}>
                      {f.label}: {f.currency || calc.currency || currency}{' '}
                      {Number(f.amount).toLocaleString()}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="border-t pt-2">
              <span className="text-sm text-muted-foreground">Total Payable:</span>
              <div className="text-lg font-bold text-primary">
                {currency} {Number(calc.total_payable || 0).toLocaleString()}
              </div>
            </div>

            {calc.warnings?.length > 0 && (
              <div className="flex items-start gap-2 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <div>
                  <strong>Warnings:</strong> {calc.warnings.join('; ')}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-end gap-3">
          <Button onClick={generateForm} disabled={!selected || generating}>
            {generating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4 mr-2" />
            )}
            Generate Transfer Form
          </Button>

          <div className="space-y-1">
            <label className="text-sm font-medium">Proof of Payment (optional)</label>
            <Input
              type="file"
              onChange={(e) => setPaidProof(e.target.files?.[0] || null)}
              accept=".pdf,.jpg,.jpeg,.png"
            />
          </div>

          <Button variant="secondary" onClick={markPaid} disabled={!selected}>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Mark Duties Paid
          </Button>
        </div>

        {/* Duty profile viewer (admin) */}
        <div className="border rounded-lg p-4 mt-6 bg-muted/10">
          <div className="font-medium mb-3">Jurisdiction Profiles (Admin Reference)</div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Jurisdiction</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Rate %</TableHead>
                  <TableHead>Rounding</TableHead>
                  <TableHead className="text-right">Min</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.map((p) => (
                  <TableRow key={p.jurisdiction}>
                    <TableCell className="font-mono">{p.jurisdiction}</TableCell>
                    <TableCell>{p.method}</TableCell>
                    <TableCell className="text-right">{p.rate_pct ?? '—'}</TableCell>
                    <TableCell>{p.rounding ?? 'NONE'}</TableCell>
                    <TableCell className="text-right">{p.min_amount ?? '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-xs">
                      {p.notes || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Edit these profiles in Settings → Tax & Duty to update rates, rounding, or fee
            schedules.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
